/**
 * UserAvatar — a person's avatar across the app (user grids, menus, headers).
 *
 * Source priority: an uploaded profile image (served from the user's
 * BinaryFile avatar) → the user's Gravatar (resolved on the frontend from
 * the email hash with `d=404`) → their initials. Each step falls through
 * to the next when the image fails to load, so a user with neither an
 * upload nor a Gravatar renders as initials.
 */

import { Avatar } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import md5 from './md5';

// Gravatar requests the image at 2× the rendered size so it stays crisp
// on high-DPI displays.
const RETINA_SCALE = 2;
const DEFAULT_SIZE = 32;

function gravatarUrl(email, sizePx) {
  if (!email) return null;
  const hash = md5(email.trim().toLowerCase());
  // d=404 makes Gravatar return a 404 (instead of a generic placeholder)
  // when the user has no avatar, which fails the <img> load and steps the
  // fallback chain to initials.
  return `https://www.gravatar.com/avatar/${hash}?s=${sizePx * RETINA_SCALE}&d=404`;
}

function initialsFor(firstName, lastName, email) {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  return (email || '').trim().charAt(0).toUpperCase();
}

export default function UserAvatar({ user, email, firstName, lastName, avatarUrl, size, sx, ...rest }) {
  const resolvedEmail = email ?? user?.email ?? '';
  const resolvedFirstName = firstName ?? user?.first_name ?? '';
  const resolvedLastName = lastName ?? user?.last_name ?? '';
  const resolvedUpload = avatarUrl ?? user?.avatar ?? null;

  // The ordered source chain: uploaded image first, then Gravatar. Falsy
  // entries are dropped so the chain only ever holds usable URLs; when it's
  // exhausted, MUI Avatar renders the initials children.
  const sources = useMemo(
    () => [resolvedUpload, gravatarUrl(resolvedEmail, size)].filter(Boolean),
    [resolvedUpload, resolvedEmail, size],
  );

  const [sourceIndex, setSourceIndex] = useState(0);
  // Reset to the top of the chain whenever the candidate sources change
  // (e.g. a fresh upload replaces the avatar URL).
  useEffect(() => setSourceIndex(0), [sources]);

  const initials = initialsFor(resolvedFirstName, resolvedLastName, resolvedEmail);
  const src = sources[sourceIndex] || undefined;

  return (
    <Avatar
      src={src}
      alt={`${resolvedFirstName} ${resolvedLastName}`.trim() || resolvedEmail}
      // Advance to the next source on load failure; once past the end,
      // src becomes undefined and the initials children render.
      imgProps={{ onError: () => setSourceIndex(index => index + 1) }}
      sx={{ width: size, height: size, fontSize: size * 0.45, ...sx }}
      {...rest}
    >
      {initials || null}
    </Avatar>
  );
}

UserAvatar.propTypes = {
  // Either pass a `user` object…
  user: PropTypes.shape({
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    avatar: PropTypes.string,
  }),
  // …or the individual fields (these win over `user` when both are given).
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  avatarUrl: PropTypes.string,
  size: PropTypes.number,
  sx: PropTypes.object,
};

UserAvatar.defaultProps = {
  user: undefined,
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  avatarUrl: undefined,
  size: DEFAULT_SIZE,
  sx: undefined,
};
